import { WCAG_AA_TEXT, contrastRatio } from './bannerContrast.js';

/**
 * Whether the balance will be readable over a chosen header image, and which colour to use.
 *
 * ## Why an operator cannot judge this by eye
 *
 * They are looking at the picture. The customer is looking at a number *on* the picture, and
 * the part that decides readability is a band near the bottom that the operator's eye slides
 * straight over. A photograph that is unmistakably dark can have a pale sky exactly where the
 * balance sits, and the first anyone hears about it is a customer who cannot read their own
 * money.
 *
 * The app also composites a dark scrim over the image before drawing the text, so the honest
 * question is not "is this photo dark" but "is this photo dark *after* the scrim", which is not
 * something anybody estimates correctly from a thumbnail.
 *
 * ## What it measures
 *
 * The region the text actually occupies, not the whole image: the bottom band, where the label
 * and the balance are drawn. Averaging the whole picture would let a bright top half drag the
 * verdict away from the half that matters.
 *
 * It samples on a small canvas rather than reading every pixel. A three-megabyte photograph is
 * tens of millions of pixels and the answer does not change: this is asking how light a region
 * is on average, and a 64-pixel-wide downscale is that average by construction, computed by the
 * browser's own image scaler.
 *
 * ## Why it advises rather than refuses
 *
 * An operator choosing brand artwork has reasons this code knows nothing about. Refusing an
 * image because a ratio came out at 4.1 would make the feature unusable for a picture that is
 * perfectly fine with a slightly stronger scrim. It says what it measured, what it recommends,
 * and whether the result clears the accessibility threshold, and then lets a person decide.
 */

/** The scrim the app paints over a header image, as alpha at the top and bottom of the band. */
const SCRIM_TOP_ALPHA = 0.15;
const SCRIM_BOTTOM_ALPHA = 0.55;

/**
 * The share of the image height the text sits in, measured from the bottom.
 *
 * Forty per cent, which covers the label and the balance with room for the descenders and the
 * cover-fit crop moving things slightly. Measuring a thinner band would be more precise about
 * a layout that is not pixel-fixed, which is precision about the wrong thing.
 */
const TEXT_BAND = 0.4;

/** How wide the sampling canvas is. The answer is an average; more pixels do not improve it. */
const SAMPLE_WIDTH = 64;

export interface HeroLegibility {
  /** The colour to draw the balance in. */
  readonly recommended: 'light' | 'dark';
  /** Contrast of white text against the scrimmed image, in the band the text occupies. */
  readonly whiteRatio: number;
  /** The same for near-black text. */
  readonly darkRatio: number;
  /** True when the recommendation clears the WCAG AA threshold for ordinary text. */
  readonly passes: boolean;
  /** One sentence for the operator, with no jargon and no ratio in it. */
  readonly advice: string;
}

/**
 * Measure a loaded image.
 *
 * Returns null when the browser will not give up the pixels, which happens for an image loaded
 * cross-origin without CORS. That is a real state on this screen: the stored header is fetched
 * from the API's origin. A null means "no advice", never "it is fine" - the caller shows
 * nothing rather than a reassurance it has not earned.
 */
export function measureHeroLegibility(image: HTMLImageElement): HeroLegibility | null {
  const width = SAMPLE_WIDTH;
  const height = Math.max(1, Math.round((image.naturalHeight / image.naturalWidth) * width));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) return null;

  context.drawImage(image, 0, 0, width, height);

  const bandTop = Math.max(0, Math.floor(height * (1 - TEXT_BAND)));
  let pixels: Uint8ClampedArray;
  try {
    pixels = context.getImageData(0, bandTop, width, height - bandTop).data;
  } catch {
    // A tainted canvas. The image is cross-origin and its pixels are not readable, which is a
    // reason to say nothing rather than to guess.
    return null;
  }

  return verdictFor(averageChannels(pixels), bandLuminanceScrim());
}

/**
 * The mean colour of a run of RGBA pixels.
 *
 * Alpha is ignored deliberately. These come from a canvas the image was drawn onto opaquely, so
 * every alpha is 255; weighting by it would be arithmetic that can only ever divide by one.
 */
function averageChannels(pixels: Uint8ClampedArray): readonly [number, number, number] {
  let r = 0;
  let g = 0;
  let b = 0;
  const count = pixels.length / 4;
  for (let i = 0; i < pixels.length; i += 4) {
    r += pixels[i] as number;
    g += pixels[i + 1] as number;
    b += pixels[i + 2] as number;
  }
  return [r / count, g / count, b / count];
}

/**
 * The scrim's average opacity across the text band.
 *
 * The app paints a vertical gradient from `SCRIM_TOP_ALPHA` to `SCRIM_BOTTOM_ALPHA` over the
 * whole header. The text band is the bottom `TEXT_BAND` of it, so the gradient is already well
 * into its darker half there, and averaging over the band is what a reader's eye does anyway.
 */
function bandLuminanceScrim(): number {
  const alphaAtBandTop = SCRIM_TOP_ALPHA + (SCRIM_BOTTOM_ALPHA - SCRIM_TOP_ALPHA) * (1 - TEXT_BAND);
  return (alphaAtBandTop + SCRIM_BOTTOM_ALPHA) / 2;
}

/** The verdict for a measured average colour under a given scrim opacity. */
export function verdictFor(
  average: readonly [number, number, number],
  scrimAlpha: number,
): HeroLegibility {
  // The scrim is black at `scrimAlpha`, so every channel is scaled toward zero. This is the
  // colour the customer actually sees behind the balance, which is the only one worth measuring.
  const scrimmed = average.map((channel) => channel * (1 - scrimAlpha)) as unknown as readonly [
    number,
    number,
    number,
  ];
  const hex = toHex(scrimmed);

  const whiteRatio = contrastRatio('#FFFFFF', hex) ?? 1;
  // Near-black rather than pure black, because that is what the app would actually draw: pure
  // black on a photograph reads as a hole rather than as text.
  const darkRatio = contrastRatio('#111111', hex) ?? 1;

  const recommended = whiteRatio >= darkRatio ? 'light' : 'dark';
  const best = Math.max(whiteRatio, darkRatio);
  const passes = best >= WCAG_AA_TEXT;

  return {
    recommended,
    whiteRatio,
    darkRatio,
    passes,
    advice: adviceFor(recommended, passes),
  };
}

/**
 * The sentence an operator reads.
 *
 * No ratio and no standard named. "4.1:1 against WCAG AA" is precise and tells somebody
 * choosing a photograph nothing they can act on; "the balance will be hard to read on this
 * one, try a darker picture" tells them exactly what to do next.
 */
function adviceFor(recommended: 'light' | 'dark', passes: boolean): string {
  if (!passes) {
    return recommended === 'light'
      ? 'The balance will be hard to read on this image. Try a darker picture, or one with a plain area near the bottom.'
      : 'The balance will be hard to read on this image. Try a lighter picture, or one with a plain area near the bottom.';
  }
  return recommended === 'light'
    ? 'White text reads well on this image.'
    : 'This image is light near the bottom, so dark text would read better than white.';
}

function toHex(channels: readonly [number, number, number]): string {
  const part = (value: number): string =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0');
  return `#${part(channels[0])}${part(channels[1])}${part(channels[2])}`.toUpperCase();
}
